import moment from 'moment';
import { t } from '../i18n';

export function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}

export function formatStatString(statString) {
  if (statString == 'ShotsAtLowVelocityRate' || statString == 'shots_at_low_velocity_rate') return t('shared.formatters.full_movement_accuracy');
  if (statString == 'velocity_control_rate') return t('shared.formatters.velocity_control');

  if (!statString) return '';
  return statString
    // Add spaces before capital letters
    .replace(/([A-Z])/g, ' $1')
    // Remove any underscores and trim extra spaces
    .replace(/_/g, ' ').trim()
    // Convert to lowercase
    .toLowerCase();
}

export function formatData(val, type) {
  let formattedStat;
  const value = Math.abs(parseFloat(val));

  switch (type) {
    case 'integer':
      formattedStat = value.toFixed(2);
      break;
    case 'percentage':
      formattedStat = value === 1 ? 100 : (value * 100).toFixed(1);
      break;
    case 'decimal':
      formattedStat = value.toFixed(2);
      break;
    case 'degrees':
      formattedStat = value.toFixed(1);
      break;
    case 'seconds':
      formattedStat = value.toFixed(2);
      break;
    case 'nanoseconds':
      formattedStat = (value / 1000000000).toFixed(2);
      break;
    default:
      formattedStat = value;
      break;
  }

  return formattedStat;
}

export function formatDataString(val, type) {
  let formattedStat;
  const value = Math.abs(parseFloat(val));

  switch (type) {
    case 'integer':
      formattedStat = parseFloat(value.toFixed(2));
      break;
    case 'percentage':
      formattedStat = `${(value * 100).toFixed(1)}%`;
      break;
    case 'decimal':
      formattedStat = value.toFixed(2);
      break;
    case 'degrees':
      formattedStat = `${value.toFixed(1)}°`;
      break;
    case 'seconds':
      formattedStat = `${value.toFixed(2)}s`;
      break;
    case 'nanoseconds':
      formattedStat = `${(value / 1000000000).toFixed(2)}s`;
      break;
    default:
      formattedStat = '';
      break;
  }

  return formattedStat;
}

// Sign-aware wrapper around formatDataString. Shared by the coach stat cells and
// the breakdown popover so a trigger and its popover header never disagree.
// Named apart from the several component-local formatStatValue helpers, one of
// which takes the same arguments but maps 0 to "no data".
export function formatBreakdownValue(value, unitType) {
  const sign = value < 0 ? '−' : '';
  const formatted = formatDataString(Math.abs(value), unitType);
  if (formatted === '' || String(formatted).includes('NaN')) return String(value);
  return sign + formatted;
}

export function formatStatStringData(val, type) {
  const formattedStat = formatData(val, type);

  switch (type) {
    case 'percentage':
      return `${formattedStat}%`;
    case 'degrees':
      return `${formattedStat}°`;
    case 'seconds':
      return `${formattedStat}s`;
    default:
      return formattedStat.toString();
  }
}

/**
 * Calculates K/D ratio
 * @param {number} kills - Number of kills
 * @param {number} deaths - Number of deaths
 * @returns {string} Formatted K/D ratio
 */
export function calculateKDRatio(kills, deaths) {
  if (!deaths || deaths === 0) return '0.00';
  return (kills / deaths).toFixed(2);
}

/**
 * Formats K/D/A display
 * @param {number} kills - Number of kills
 * @param {number} deaths - Number of deaths
 * @param {number} assists - Number of assists
 * @returns {string} Formatted K/D/A string
 */
export function formatKDA(kills, deaths, assists) {
  return `${kills} / ${deaths} / ${assists}`;
}

/**
 * Converts nanoseconds to seconds
 * @param {number} nanoseconds - Time in nanoseconds
 * @returns {string} Formatted time in seconds
 */
export function convertNanoSeconds(nanoseconds) {
  return (nanoseconds / 1000000000).toFixed(2);
}

/**
 * Gets the sort value for a column
 * @param {object} player - Player data
 * @param {object} column - Column configuration
 * @returns {number} The value to sort by
 */
export function getSortValue(player, column) {
  const matchPerformance = player.match_performance;

  if (column.key === 'kd_ratio') {
    return matchPerformance.kills / (matchPerformance.deaths || 1);
  }

  if (column.key === 'kills' && column.formatter === 'kda') {
    return matchPerformance.kills;
  }

  return matchPerformance[column.key] || 0;
}

/**
 * Gets the color class based on deviation from target average
 * @param {number} deviation - The deviation value
 * @param {boolean} isWorseWhenHigher - Whether higher values indicate worse performance (e.g., time-based stats)
 * @returns {string} The CSS color class
 */
export function getStatColorClass(deviation, isWorseWhenHigher = false) {
  // If the stat is worse when higher, invert the deviation
  const adjustedDeviation = isWorseWhenHigher ? -deviation : deviation;

  switch (true) {
    case adjustedDeviation >= 0.4:
      return 'text-success-400';
    case adjustedDeviation >= 0.15:
      return 'text-[#9ccc70]';
    case adjustedDeviation >= 0:
      return 'text-text-primary';
    case adjustedDeviation >= -0.15:
      return 'text-text-primary';
    case adjustedDeviation >= -0.4:
      return 'text-yellow-400';
    case adjustedDeviation >= -1:
      return 'text-warning-400';
    default:
      return 'text-danger-400';
  }
}

/**
 * Formats a unix timestamp (in milliseconds) to a time string
 * @param {number|string} unixTimestamp - Unix timestamp in milliseconds
 * @param {string} format - Moment.js format string (default: 'HH:mm')
 * @returns {string} Formatted time string
 */
export function formatUnixTime(unixTimestamp, format = 'HH:mm') {
  if (!unixTimestamp) return '';

  // Convert to number if it's a string
  const timestamp = typeof unixTimestamp === 'string' ? parseInt(unixTimestamp) : unixTimestamp;

  // Check if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  const adjustedTimestamp = timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;

  return moment(adjustedTimestamp).format(format);
}
